import { captureMessage } from '@sentry/react';

/**
 * `/giris` adresinde giriş sonrası dönülecek adresi taşıyan query parametresinin adı.
 * OAuth `authorize` ucu oturumsuz isteği bu parametreyle giriş ekranına yollar; TYS girişini
 * kendi içinden başlatan uygulamalar (Word eklentisi gibi) da aynı parametreyi kullanır.
 */
export const RETURN_TO_PARAM = 'returnTo';

/**
 * Dış dönüş adresinin saklandığı sessionStorage anahtarı.
 * Uygulama içi yönlendirme için kullanılan `returnURL` anahtarından ayrı tutulur: o anahtarı
 * okuyan yerler değeri `navigate()`e verdiği için dış adresler orada rota sanılıp kırılır.
 */
const STORAGE_KEY = 'externalReturnTo';

/**
 * Bir adresi karşılaştırmaya uygun origin'e indirger, geçersizse null döner.
 * @param {string} value - Tam adres veya origin.
 * @returns {string | null}
 */
const toOrigin = (value) => {
  try {
    return new URL(value.trim()).origin;
  } catch {
    return null;
  }
};

/**
 * Env'de tanımlı izinli dış origin listesi. Yalnızca uygulama dışındaki adresler için
 * gerekir; tanımlı değilse boş liste döner ve hiçbir dış origin kabul edilmez.
 * @returns {string[]}
 */
const getAllowedOrigins = () => (process.env.REACT_APP_ALLOWED_RETURN_ORIGINS || '').split(',').map(toOrigin).filter(Boolean);

/**
 * Dönüş adresinin izinli olup olmadığını söyler. Yalnızca http(s) şemasına izin verilir;
 * böylece `javascript:`, `data:` ve protokol-göreli (`//adres`) biçimler reddedilir.
 * Uygulamanın kendi origin'i her zaman kabul edilir — OAuth `authorize` ucu TYS ile aynı
 * origin'de olduğu için asıl akış buraya düşer ve aynı origin'e dönmek açık yönlendirme
 * riski taşımaz. Farklı bir origin ise yalnızca allowlist'te yer alıyorsa geçer.
 * @param {string | null | undefined} value - Doğrulanacak adres.
 * @returns {boolean}
 */
export const isAllowedReturnTo = (value) => {
  if (!value) return false;

  let url;

  try {
    url = new URL(value);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;

  if (url.origin === window.location.origin) return true;

  return getAllowedOrigins().includes(url.origin);
};

/**
 * Adres çubuğundan gelen dönüş adresini doğrulayıp saklar. İzinli değilse sessizce
 * yok sayar ve Sentry'ye uyarı bırakır — kullanıcının girişi normal akışla sürer.
 * @param {string | null | undefined} value - `returnTo` parametresinin değeri.
 * @returns {void}
 */
export const storeReturnTo = (value) => {
  if (!value) return;

  if (!isAllowedReturnTo(value)) {
    captureMessage(`İzinli olmayan returnTo adresi reddedildi: ${value}`, 'warning');
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, value);
};

/**
 * Saklanan dış dönüş adresini döndürür. Değer okunurken yeniden doğrulanır; allowlist
 * değişmiş veya değer elle oynanmışsa temizlenip null dönülür.
 * @returns {string | null}
 */
export const getReturnTo = () => {
  const value = sessionStorage.getItem(STORAGE_KEY);

  if (!value) return null;

  if (!isAllowedReturnTo(value)) {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }

  return value;
};

/**
 * Dış dönüş adresine çıkar. Router dış adresleri rota sanacağı için `navigate()` değil
 * `window.location.replace()` kullanılır; giriş ekranı tarayıcı geçmişinde bırakılmaz.
 * @param {string} value - `getReturnTo` ile doğrulanmış adres.
 * @returns {void}
 */
export const redirectToReturnTo = (value) => {
  sessionStorage.removeItem(STORAGE_KEY);
  window.location.replace(value);
};
